import { orthoMetersPerPixelAtom } from '@/atoms/viewer.atom';
import { useAtomValue } from 'jotai';
import { FunctionComponent, useEffect, useRef, useState } from 'react';

interface OrthoScaleBarProps {
  projectId: string;
}

const TARGET_PIXELS = 120;

// Round target world distance to a 1/2/5 × 10^k value so the bar reads as a
// clean number on screen.
const niceRound = (target: number): { value: number; lead: 1 | 2 | 5 } => {
  if (!isFinite(target) || target <= 0) return { value: 1, lead: 1 };
  const pow = Math.pow(10, Math.floor(Math.log10(target)));
  const norm = target / pow;
  if (norm < 1.5) return { value: 1 * pow, lead: 1 };
  if (norm < 3.5) return { value: 2 * pow, lead: 2 };
  if (norm < 7.5) return { value: 5 * pow, lead: 5 };
  return { value: 10 * pow, lead: 1 };
};

const formatLabel = (meters: number): string => {
  if (meters >= 1000) {
    const km = meters / 1000;
    return `${km % 1 === 0 ? km.toFixed(0) : km.toFixed(1)} km`;
  }
  if (meters >= 1) {
    return `${meters % 1 === 0 ? meters.toFixed(0) : meters.toFixed(1)} m`;
  }
  const cm = meters * 100;
  return `${cm % 1 === 0 ? cm.toFixed(0) : cm.toFixed(1)} cm`;
};

const OrthoScaleBar: FunctionComponent<OrthoScaleBarProps> = ({ projectId }) => {
  const metersPerPixel = useAtomValue(orthoMetersPerPixelAtom(projectId));

  // Throttle renders so the bar doesn't churn while zooming.
  const [throttled, setThrottled] = useState<number | null>(metersPerPixel);
  const lastRef = useRef(0);
  useEffect(() => {
    const now = Date.now();
    if (now - lastRef.current < 100) {
      const t = setTimeout(() => {
        lastRef.current = Date.now();
        setThrottled(metersPerPixel);
      }, 100);
      return () => clearTimeout(t);
    }
    lastRef.current = now;
    setThrottled(metersPerPixel);
  }, [metersPerPixel]);

  if (throttled == null || !isFinite(throttled) || throttled <= 0) return null;

  const targetMeters = throttled * TARGET_PIXELS;
  const { value: niceMeters, lead } = niceRound(targetMeters);
  const widthPx = Math.round(niceMeters / throttled);
  // Segments aligned to the leading digit so every tick lands on a clean value:
  // 1×10ⁿ → halves, 2×10ⁿ → halves, 5×10ⁿ → fifths.
  const segments = lead === 5 ? 5 : 2;

  return (
    <div className='bg-black/60 backdrop-blur-sm text-white/80 text-[9px] rounded px-1.5 py-0.5 pointer-events-none select-none flex flex-col items-start gap-0.5 leading-none'>
      <span className='font-mono'>{formatLabel(niceMeters)}</span>
      <div
        className='h-1.5 border border-white/80 border-t-0 flex'
        style={{ width: `${widthPx}px` }}
        aria-hidden>
        {Array.from({ length: segments }, (_, i) => (
          <div key={i} className={`flex-1 ${i % 2 === 0 ? 'bg-white/80' : ''}`} />
        ))}
      </div>
    </div>
  );
};

export default OrthoScaleBar;
